import React from "react";
import { Link } from "react-router-dom";

import { JsonLd } from "../components/common/JsonLd";
import { RelatedPages } from "../components/common/RelatedPages";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { TeamPreviewSection } from "../components/common/TeamPreviewSection";
import { TreatmentInfoBanner } from "../components/common/TreatmentInfoBanner";
import { LogoSvg } from "../components/svgs/LogoSvg";
import { InstagramSection } from "../components/common/InstagramSection";
import { PrimaryButton } from "../components/common/PrimaryButton";
import {
  PATH_GROUP_THERAPY,
  PATH_NEUROLOGIE_GERIATRIE,
  PATH_PSYCHIATRIE,
  PATH_START,
} from "../utils/constants";
import groupsData from "../data/groups.json";
import pageContent from "../data/pages/start.json";
import { Group } from "../types/group";
import { buildSeoPageGraph } from "../utils/structured-data";

function linkSpecialties(text: string): React.ReactNode[] {
  return text.split(/(Neurologie|Psychiatrie)/).map((part, index) => {
    if (part === "Neurologie") {
      return (
        <Link
          key={index}
          to={PATH_NEUROLOGIE_GERIATRIE}
          className="font-medium text-gray-700 dark:text-gray-200 hover:text-ergo-600 dark:hover:text-ergo-400"
        >
          Neurologie
        </Link>
      );
    }
    if (part === "Psychiatrie") {
      return (
        <Link
          key={index}
          to={PATH_PSYCHIATRIE}
          className="font-medium text-gray-700 dark:text-gray-200 hover:text-ergo-600 dark:hover:text-ergo-400"
        >
          Psychiatrie
        </Link>
      );
    }
    return part;
  });
}

export default function StartPage() {
  const missionTexts = pageContent.sections[0]?.texts ?? [];

  return (
    <>
      <SEOHelmet page="start" pathname={PATH_START} />
      <JsonLd
        data={buildSeoPageGraph(PATH_START, "start", {
          includeOfferCatalog: true,
          includeFounder: true,
        })}
      />

      <main className="space-y-16 sm:space-y-24">
        {/* Hero Section */}
        <section>
          <div className="lg:grid lg:grid-cols-12 lg:gap-x-10 xl:gap-x-14 items-center">
            <div className="lg:col-span-7 xl:col-span-6">
              <header className="mb-6">
                <LogoSvg
                  classNames="h-12 w-auto fill-current text-ergo-500 dark:text-ergo-400"
                  aria-label="Ergotherapie Rottweil Logo"
                />
              </header>
              <h1 className="text-3xl sm:text-5xl lg:text-5xl font-semibold tracking-tight leading-[1.15] text-gray-900 dark:text-white">
                Einzigartige Menschen, <br className="hidden sm:block" />
                <span className="text-ergo-600 dark:text-ergo-400">individuelle Therapien.</span>
              </h1>
              <div className="mt-8 sm:mt-10 space-y-4">
                {pageContent.heroParagraphs.map((paragraph, index) => (
                  <p key={index} className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                    {"emphasis" in paragraph ? (
                      <>
                        {paragraph.before}
                        <span className="font-medium text-gray-700 dark:text-gray-200">{paragraph.emphasis}</span>
                      </>
                    ) : (
                      paragraph.text
                    )}
                  </p>
                ))}
              </div>
            </div>
            <aside className="mt-10 lg:mt-0 lg:col-span-5 xl:col-span-6">
              <img
                src={process.env.PUBLIC_URL + "/images/tree.jpeg"}
                alt="Ergotherapie Rottweil - Moderne Praxisräume in ruhiger Umgebung mit Außenbereich"
                className="w-full rounded-2xl object-cover shadow-md aspect-[4/3] max-h-[500px]"
              />
            </aside>
          </div>
        </section>

        {/* Mission Statement Section */}
        <section aria-label="Unsere Mission">
          <div className="lg:grid lg:grid-cols-2 gap-x-12 gap-y-6">
            {missionTexts.map((text, index) => (
              <p key={index} className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                {index === missionTexts.length - 1 ? linkSpecialties(text) : text}
              </p>
            ))}
          </div>

          <div className="mt-12">
            <TreatmentInfoBanner />
          </div>
        </section>

        {/* Gruppentherapien CTA */}
        <section aria-label="Gruppentherapien Hinweis">
          <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-800/60 shadow-sm p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                Gruppentherapien
              </h2>
              <p className="mt-2 text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-2xl">
                Neu: Unsere Gruppentherapien – kompakt vorgestellt mit Inhalten und PDF.
              </p>
              <Link
                to={PATH_GROUP_THERAPY}
                className="group inline-block mt-5"
                aria-label="Gruppentherapien Vorschau öffnen"
              >
                <div className="flex items-center -space-x-4">
                  {((groupsData as { groups: Group[] }).groups).map((g, i) => (
                    <img
                      key={g.id}
                      src={g.coverImage || `/images/gruppen/${g.id}-cover.jpg`}
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        el.src = `/images/gruppen/${g.id}-cover-1.jpg`;
                      }}
                      alt={
                        g.description
                          ? `Gruppentherapie: ${g.description.substring(0, 100)}...`
                          : `Gruppentherapie ${g.title} in Rottweil`
                      }
                      className={`h-24 w-18 sm:h-28 sm:w-20 object-cover rounded-xl ring-2 ring-white dark:ring-gray-900 shadow-sm transition-transform duration-300 ${i % 3 === 0 ? "rotate-[-2deg]" : i % 3 === 1 ? "rotate-0" : "rotate-[2deg]"} group-hover:-translate-y-0.5`}
                    />
                  ))}
                </div>
              </Link>
            </div>
            <div className="flex-shrink-0">
              <PrimaryButton to={PATH_GROUP_THERAPY}>Zur Übersicht</PrimaryButton>
            </div>
          </div>
        </section>

        <TeamPreviewSection />
        <InstagramSection />
        <RelatedPages pathname={PATH_START} />
      </main>
    </>
  );
}
